/**
 * JomBaca Tadika: an original 8-level Malay reading course for ages 4–6, built on the common
 * syllable method (vowels → open syllables → words → closed syllables → digraphs → stories).
 * Every word, sentence and story here is written for JomCerdik; nothing is copied from any book.
 * Pictures are emoji so they render everywhere without image files.
 */
export type BacaWord = { w: string; s: string[]; e: string; /** picture id when it is not `baca/<word>` (English words) */ art?: string };
export type BacaStory = { title: string; e: string; art?: string; lines: string[]; questions: { q: string; options: string[]; answer: number }[] };
export type BacaLevel = {
  level: number;
  title: string;
  focus: string;
  emoji: string;
  tone: string;
  /** Syllable cards, grouped (one row per consonant, or per pattern). */
  groups: { label: string; items: string[] }[];
  words: BacaWord[];
  sentences: string[];
  stories?: BacaStory[];
  /** Heading above the tiles (English: Letters / Word families). */
  groupsTitle?: string;
};

const VOWELS = ["a", "e", "i", "o", "u"];
const open = (c: string) => VOWELS.map((v) => c + v);

export const BACA_LEVELS: BacaLevel[] = [
  {
    level: 1,
    title: "Vokal & b, c, d, g, h",
    focus: "Kenal lima huruf vokal dan suku kata terbuka pertama.",
    emoji: "🅰️",
    tone: "from-rose-400 to-pink-500",
    groups: [
      { label: "Vokal", items: VOWELS },
      { label: "b", items: open("b") },
      { label: "c", items: open("c") },
      { label: "d", items: open("d") },
      { label: "g", items: open("g") },
      { label: "h", items: open("h") },
    ],
    words: [
      { w: "dadu", s: ["da", "du"], e: "🎲" },
      { w: "gigi", s: ["gi", "gi"], e: "🦷" },
      { w: "cuci", s: ["cu", "ci"], e: "🧼" },
      { w: "hidu", s: ["hi", "du"], e: "👃" },
      { w: "guci", s: ["gu", "ci"], e: "🏺" },
      { w: "cucu", s: ["cu", "cu"], e: "👶" },
      { w: "bahu", s: ["ba", "hu"], e: "🤷" },
    ],
    sentences: ["cuci gigi", "cucu cuci gigi"],
  },
  {
    level: 2,
    title: "j, k, l, m, n, p",
    focus: "Enam huruf baharu. Mula baca perkataan dua suku kata.",
    emoji: "📘",
    tone: "from-orange-400 to-amber-500",
    groups: ["j", "k", "l", "m", "n", "p"].map((c) => ({ label: c, items: open(c) })),
    words: [
      { w: "buku", s: ["bu", "ku"], e: "📘" },
      { w: "bola", s: ["bo", "la"], e: "⚽" },
      { w: "baju", s: ["ba", "ju"], e: "👕" },
      { w: "kaki", s: ["ka", "ki"], e: "🦶" },
      { w: "kuda", s: ["ku", "da"], e: "🐴" },
      { w: "madu", s: ["ma", "du"], e: "🍯" },
      { w: "kopi", s: ["ko", "pi"], e: "☕" },
      { w: "cili", s: ["ci", "li"], e: "🌶️" },
      { w: "padi", s: ["pa", "di"], e: "🌾" },
      { w: "paku", s: ["pa", "ku"], e: "🔩" },
      { w: "lima", s: ["li", "ma"], e: "5️⃣" },
    ],
    sentences: ["Ini buku.", "Ini bola.", "Ini baju.", "Ini kaki kuda.", "Ini madu."],
  },
  {
    level: 3,
    title: "r, s, t, w, y",
    focus: "Lengkapkan suku kata terbuka. Baca ayat pendek.",
    emoji: "🍞",
    tone: "from-lime-400 to-green-500",
    groups: ["r", "s", "t", "w", "y"].map((c) => ({ label: c, items: open(c) })),
    words: [
      { w: "roti", s: ["ro", "ti"], e: "🍞" },
      { w: "susu", s: ["su", "su"], e: "🥛" },
      { w: "topi", s: ["to", "pi"], e: "🧢" },
      { w: "rusa", s: ["ru", "sa"], e: "🦌" },
      { w: "sate", s: ["sa", "te"], e: "🍢" },
      { w: "lori", s: ["lo", "ri"], e: "🚚" },
      { w: "kayu", s: ["ka", "yu"], e: "🪵" },
      { w: "batu", s: ["ba", "tu"], e: "🪨" },
      { w: "sawi", s: ["sa", "wi"], e: "🥬" },
      { w: "yoyo", s: ["yo", "yo"], e: "🪀" },
      { w: "ratu", s: ["ra", "tu"], e: "👑" },
      { w: "tali", s: ["ta", "li"], e: "🪢" },
    ],
    sentences: ["Saya suka roti.", "Itu lori biru.", "Ini topi saya.", "Rusa itu lari.", "Ratu suka sate."],
  },
  {
    level: 4,
    title: "Perkataan & ayat",
    focus: "Gabung suku kata jadi perkataan, perkataan jadi ayat.",
    emoji: "👩",
    tone: "from-teal-400 to-emerald-500",
    groups: [
      { label: "Vokal + suku kata", items: ["i-bu", "a-pi", "u-bi", "a-ku", "i-tu", "i-ni"] },
      { label: "Suku kata + suku kata", items: ["ba-pa", "ba-ca", "su-ka", "ka-ri", "bi-ru", "la-ju"] },
    ],
    words: [
      { w: "ibu", s: ["i", "bu"], e: "👩" },
      { w: "bapa", s: ["ba", "pa"], e: "👨" },
      { w: "api", s: ["a", "pi"], e: "🔥" },
      { w: "ubi", s: ["u", "bi"], e: "🍠" },
      { w: "kari", s: ["ka", "ri"], e: "🍛" },
      { w: "jari", s: ["ja", "ri"], e: "☝️" },
      { w: "baca", s: ["ba", "ca"], e: "📖" },
      { w: "biru", s: ["bi", "ru"], e: "🔵" },
      { w: "kuku", s: ["ku", "ku"], e: "💅" },
    ],
    sentences: ["Ibu baca buku.", "Bapa bawa lori.", "Dia suka kari.", "Topi bapa biru.", "Siti cuci baju.", "Kuda lari laju."],
  },
  {
    level: 5,
    title: "Suku kata tertutup",
    focus: "Suku kata yang berakhir dengan konsonan: -n, -m, -t, -k, -h, -s, -l, -r.",
    emoji: "🐟",
    tone: "from-sky-400 to-blue-500",
    groups: [
      { label: "-n -m", items: ["kan", "num", "bun", "pin", "yam", "lam"] },
      { label: "-t -k -h", items: ["tak", "tik", "sut", "mah", "ah", "kek"] },
      { label: "-s -l -r", items: ["bas", "kus", "nas", "pal", "lar", "lur"] },
    ],
    words: [
      { w: "ikan", s: ["i", "kan"], e: "🐟" },
      { w: "ayam", s: ["a", "yam"], e: "🐔" },
      { w: "rumah", s: ["ru", "mah"], e: "🏠" },
      { w: "katak", s: ["ka", "tak"], e: "🐸" },
      { w: "itik", s: ["i", "tik"], e: "🦆" },
      { w: "bas", s: ["bas"], e: "🚌" },
      { w: "jam", s: ["jam"], e: "⏰" },
      { w: "kek", s: ["kek"], e: "🎂" },
      { w: "telur", s: ["te", "lur"], e: "🥚" },
      { w: "lampu", s: ["lam", "pu"], e: "💡" },
      { w: "tikus", s: ["ti", "kus"], e: "🐭" },
      { w: "ular", s: ["u", "lar"], e: "🐍" },
      { w: "nanas", s: ["na", "nas"], e: "🍍" },
      { w: "kasut", s: ["ka", "sut"], e: "👟" },
      { w: "sabun", s: ["sa", "bun"], e: "🧼" },
      { w: "pintu", s: ["pin", "tu"], e: "🚪" },
    ],
    sentences: ["Adik makan ikan.", "Bapa naik bas.", "Ibu beli kek.", "Katak itu kecil.", "Kasut saya baru.", "Tikus lari laju."],
  },
  {
    level: 6,
    title: "Tiga suku kata",
    focus: "Perkataan yang lebih panjang dan ayat yang lebih panjang.",
    emoji: "🚗",
    tone: "from-indigo-400 to-violet-500",
    groups: [
      { label: "Contoh", items: ["ke-re-ta", "ke-la-pa", "ba-si-kal", "se-ko-lah", "to-ma-to", "ben-de-ra"] },
    ],
    words: [
      { w: "kereta", s: ["ke", "re", "ta"], e: "🚗" },
      { w: "kelapa", s: ["ke", "la", "pa"], e: "🥥" },
      { w: "kerusi", s: ["ke", "ru", "si"], e: "🪑" },
      { w: "sekolah", s: ["se", "ko", "lah"], e: "🏫" },
      { w: "basikal", s: ["ba", "si", "kal"], e: "🚲" },
      { w: "kamera", s: ["ka", "me", "ra"], e: "📷" },
      { w: "tomato", s: ["to", "ma", "to"], e: "🍅" },
      { w: "telefon", s: ["te", "le", "fon"], e: "📱" },
      { w: "kelawar", s: ["ke", "la", "war"], e: "🦇" },
      { w: "bendera", s: ["ben", "de", "ra"], e: "🚩" },
      { w: "kemeja", s: ["ke", "me", "ja"], e: "👔" },
      { w: "komputer", s: ["kom", "pu", "ter"], e: "💻" },
    ],
    sentences: ["Bapa cuci kereta.", "Saya naik basikal ke sekolah.", "Kelawar keluar pada malam hari.", "Adik makan tomato merah.", "Kakak ambil gambar dengan kamera."],
  },
  {
    level: 7,
    title: "ng, ny, sy, kh & ai, au, oi",
    focus: "Dua huruf satu bunyi, dan bunyi diftong.",
    emoji: "🌸",
    tone: "from-fuchsia-400 to-purple-500",
    groups: [
      { label: "ng", items: ["nga", "ngi", "ngu", "ang", "ing", "ung"] },
      { label: "ny", items: ["nya", "nyi", "nyu"] },
      { label: "sy / kh", items: ["sya", "syi", "kha", "khe"] },
      { label: "ai / au / oi", items: ["tai", "dai", "pai", "sau", "mau", "doi"] },
    ],
    words: [
      { w: "bunga", s: ["bu", "nga"], e: "🌸" },
      { w: "kucing", s: ["ku", "cing"], e: "🐱" },
      { w: "burung", s: ["bu", "rung"], e: "🐦" },
      { w: "pisang", s: ["pi", "sang"], e: "🍌" },
      { w: "jagung", s: ["ja", "gung"], e: "🌽" },
      { w: "payung", s: ["pa", "yung"], e: "☂️" },
      { w: "singa", s: ["si", "nga"], e: "🦁" },
      { w: "nyamuk", s: ["nya", "muk"], e: "🦟" },
      { w: "penyu", s: ["pe", "nyu"], e: "🐢" },
      { w: "syiling", s: ["syi", "ling"], e: "🪙" },
      { w: "khemah", s: ["khe", "mah"], e: "⛺" },
      { w: "pantai", s: ["pan", "tai"], e: "🏖️" },
      { w: "kedai", s: ["ke", "dai"], e: "🏪" },
      { w: "tupai", s: ["tu", "pai"], e: "🐿️" },
      { w: "pisau", s: ["pi", "sau"], e: "🔪" },
      { w: "harimau", s: ["ha", "ri", "mau"], e: "🐯" },
      { w: "pulau", s: ["pu", "lau"], e: "🏝️" },
      { w: "kerbau", s: ["ker", "bau"], e: "🐃" },
    ],
    sentences: ["Bunga itu cantik.", "Kucing tidur di atas kerusi.", "Nyamuk itu kecil.", "Kami pergi ke pantai.", "Harimau tinggal di hutan.", "Ibu beli pisang di kedai."],
  },
  {
    level: 8,
    title: "Cerita pendek",
    focus: "Baca cerita, kemudian jawab soalan.",
    emoji: "📚",
    tone: "from-amber-400 to-orange-500",
    groups: [],
    words: [
      { w: "padang", s: ["pa", "dang"], e: "🏟️" },
      { w: "rambutan", s: ["ram", "bu", "tan"], e: "🔴" },
      { w: "pasir", s: ["pa", "sir"], e: "⏳" },
      { w: "datuk", s: ["da", "tuk"], e: "👴" },
      { w: "pokok", s: ["po", "kok"], e: "🌳" },
      { w: "istana", s: ["is", "ta", "na"], e: "🏰" },
    ],
    sentences: [],
    stories: [
      {
        title: "Bola Merah Adi",
        e: "bola",
        lines: ["Adi ada bola merah.", "Dia main bola di padang.", "Bola itu masuk ke dalam longkang.", "Ayah tolong ambil bola itu.", "Adi ucap terima kasih kepada ayah."],
        questions: [
          { q: "Apakah warna bola Adi?", options: ["merah", "biru", "hijau"], answer: 0 },
          { q: "Siapa tolong ambil bola itu?", options: ["ibu", "ayah", "kakak"], answer: 1 },
        ],
      },
      {
        title: "Kucing Siti",
        e: "kucing",
        lines: ["Siti ada seekor kucing.", "Nama kucing itu Tompok.", "Tompok suka makan ikan.", "Pada waktu petang, Tompok tidur di atas kerusi.", "Siti sayang Tompok."],
        questions: [
          { q: "Apakah nama kucing Siti?", options: ["Comel", "Oren", "Tompok"], answer: 2 },
          { q: "Tompok suka makan apa?", options: ["ikan", "roti", "jagung"], answer: 0 },
        ],
      },
      {
        title: "Ke Pantai",
        e: "🏖️",
        lines: ["Hari Sabtu, keluarga Ali pergi ke pantai.", "Mereka naik kereta.", "Di pantai, Ali bina istana pasir.", "Adik kutip kulit kerang.", "Mereka pulang pada waktu petang."],
        questions: [
          { q: "Bilakah keluarga Ali pergi ke pantai?", options: ["hari Isnin", "hari Sabtu", "hari Jumaat"], answer: 1 },
          { q: "Apakah yang Ali bina?", options: ["istana pasir", "rumah kayu", "kereta"], answer: 0 },
        ],
      },
      {
        title: "Pokok Rambutan Datuk",
        e: "🌳",
        lines: ["Datuk ada sebatang pokok rambutan.", "Buah rambutan sudah masak.", "Warnanya merah.", "Mira dan datuk petik buah itu.", "Mereka makan rambutan bersama-sama."],
        questions: [
          { q: "Pokok apakah yang datuk ada?", options: ["durian", "mangga", "rambutan"], answer: 2 },
          { q: "Siapakah yang petik buah itu?", options: ["Mira dan datuk", "Ali dan ayah", "ibu dan kakak"], answer: 0 },
        ],
      },
    ],
  },
];

export const bacaLevel = (n: number) => BACA_LEVELS.find((l) => l.level === n);

/** Every syllable a level has taught, for distractor tiles in the "cantum" game. */
export function bacaSyllables(level: BacaLevel): string[] {
  const set = new Set<string>();
  for (const w of level.words) for (const s of w.s) set.add(s);
  for (const g of level.groups) for (const it of g.items) for (const s of it.split("-")) set.add(s);
  return [...set];
}
